import type { AtLeast, Awaitable } from '#layers/base/public';
import type { BlockEventFunctionPayload, BlockSerializedChild, BlockSerializedSlotOutlet } from '#layers/block-engine/public';
import type { MonospaceBlock, MonospacePage, MonospaceSpace } from '#layers/data-model/public';

export function spaceInfoFactory(space: MonospaceSpace & {
	block?: AtLeast<Omit<NonNullable<MonospaceBlock['renderData']>, 'children'>, 'rootChildren'> & {
		children: Record<string, AtLeast<Omit<BlockSerializedChild, 'id'>, 'blockId'> | AtLeast<Omit<BlockSerializedSlotOutlet, 'id'>, 'slotName'>>;
	};
}): MonospaceSpace & {
	block?: MonospaceBlock;
} {
	const children: Record<string, BlockSerializedChild | BlockSerializedSlotOutlet> = {};

	if (space.block) {
		for (const [key, value] of Object.entries(space.block.children)) {
			if ('blockId' in value) {
				// It's a child block
				children[key] = {
					id: key,
					props: {},
					slots: {},
					...value,
				} satisfies BlockSerializedChild;
			}
			else if ('slotName' in value) {
				// It's a slot outlet
				children[key] = {
					id: key,
					props: {},
					...value,
				} satisfies BlockSerializedSlotOutlet;
			}
		}
	}

	return {
		...space,
		block: space.block
			? {
					id: `${space.id}:block`,
					category: 'space',
					spaceId: space.id,
					pageId: null,
					name: `${space.id}:block`,
					description: null,
					icon: null,
					collectionId: null,
					pageIntent: null,
					pageType: null,
					renderData: {
						variables: [],
						props: [],
						events: [],
						slots: [],
						...space.block,
						children,
					},
				}
			: undefined,
	};
}

export function spacePageFactory(page: AtLeast<MonospacePage, 'id' | 'title' | 'route' | 'spaceId'> & {
	block?: AtLeast<Omit<NonNullable<MonospaceBlock['renderData']>, 'children'>, 'rootChildren'> & {
		children: Record<string, AtLeast<Omit<BlockSerializedChild, 'id'>, 'blockId'> | AtLeast<Omit<BlockSerializedSlotOutlet, 'id'>, 'slotName'>>;
	};
}): MonospacePage & {
	block: MonospaceBlock | undefined;
} {
	return {
		icon: 'lucide:file',
		sort: 0,
		description: null,
		isFullWidth: false,
		isHidden: false,
		sectionId: null,
		type: null,
		intent: null,
		collectionId: null,
		parentPageId: null,
		overridePage: null,
		...page,
		block: page.block
			? {
					id: `${page.id}:block`,
					category: 'page',
					pageId: page.id,
					spaceId: null,
					description: null,
					collectionId: null,
					pageIntent: null,
					pageType: null,
					icon: null,
					name: 'page-details',
					renderData: blockRenderDataFactory(page.block),
				}
			: undefined,
	};
}

export function blockRenderDataFactory(block: AtLeast<Omit<NonNullable<MonospaceBlock['renderData']>, 'children'>, 'rootChildren'> & {
	children: Record<string, AtLeast<Omit<BlockSerializedChild, 'id'>, 'blockId'> | AtLeast<Omit<BlockSerializedSlotOutlet, 'id'>, 'slotName'>>;
}): NonNullable<MonospaceBlock['renderData']> {
	const children: Record<string, BlockSerializedChild | BlockSerializedSlotOutlet> = {};

	for (const [key, value] of Object.entries(block.children)) {
		if ('blockId' in value) {
			// It's a child block
			children[key] = {
				id: key,
				props: {},
				slots: {},
				...value,
			} satisfies BlockSerializedChild;
		}
		else if ('slotName' in value) {
			// It's a slot outlet
			children[key] = {
				id: key,
				props: {},
				...value,
			} satisfies BlockSerializedSlotOutlet;
		}
	}

	return {
		variables: [],
		props: [],
		events: [],
		slots: [],
		...block,
		children,
	};
}

export function blockPropsFactory<T extends Record<string, any>>(props: {
	[K in keyof T]: K extends `on${Capitalize<string>}` ? (payload: BlockEventFunctionPayload) => Awaitable<void> : T[K]
}) {
	return props;
}
